/*
 * offlineStore.js — per-trip offline image + outbox for the itinerary page.
 *
 * Backed by IndexedDB (no external dependency, keeps the bundle lean). Data is
 * managed trip-by-trip so each trip is an independent, small record that loads fast.
 *
 * Two object stores:
 *   - snapshots: one record per trip holding the RAW server payloads. Keeping the
 *     raw shape (not the grouped/normalized React state) means all merge/normalize
 *     logic stays in ItineraryPageV2's fetchTripData/applySnapshot.
 *       { tripId, trip, activities, locked, lodging, cityContexts, savedAt }
 *   - outbox: queued mutations awaiting sync, replayed FIFO on reconnect.
 *       { id (auto), tripId, method, url, body, createdAt }
 */

const DB_NAME = 'atp-offline';
const DB_VERSION = 2;
const SNAP_STORE = 'snapshots';
const OUTBOX_STORE = 'outbox';
const LIST_STORE = 'lists';

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SNAP_STORE)) {
        db.createObjectStore(SNAP_STORE, { keyPath: 'tripId' });
      }
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const os = db.createObjectStore(OUTBOX_STORE, { keyPath: 'id', autoIncrement: true });
        os.createIndex('tripId', 'tripId', { unique: false });
      }
      if (!db.objectStoreNames.contains(LIST_STORE)) {
        db.createObjectStore(LIST_STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(storeName, mode, fn) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let result;
    Promise.resolve(fn(store)).then(r => { result = r; }).catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

function reqToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// tripId is stored as a string key for consistency (route params are strings).
const key = (tripId) => String(tripId);

// ── Snapshots ────────────────────────────────────────────────────────────────

export async function getSnapshot(tripId) {
  try {
    return await tx(SNAP_STORE, 'readonly', store => reqToPromise(store.get(key(tripId))));
  } catch (_) {
    return null;
  }
}

/**
 * Save (merge) parts of a trip's raw snapshot. Only provided keys overwrite the
 * previous record, so callers can update just `activities` without re-supplying
 * lodging/cityContexts.
 */
export async function saveSnapshot(tripId, parts) {
  try {
    const existing = (await getSnapshot(tripId)) || {};
    const record = {
      ...existing,
      ...parts,
      tripId: key(tripId),
      savedAt: Date.now(),
    };
    await tx(SNAP_STORE, 'readwrite', store => reqToPromise(store.put(record)));
    return record;
  } catch (_) {
    return null;
  }
}

export async function deleteSnapshot(tripId) {
  try {
    await tx(SNAP_STORE, 'readwrite', store => reqToPromise(store.delete(key(tripId))));
    // Also drop any queued ops for a trip that no longer exists locally.
    const ops = await listOutbox(tripId);
    for (const op of ops) await dequeue(op.id);
  } catch (_) { /* ignore */ }
}

// ── Outbox ───────────────────────────────────────────────────────────────────

export async function enqueue(op) {
  try {
    const record = { ...op, tripId: key(op.tripId), createdAt: op.createdAt || Date.now() };
    return await tx(OUTBOX_STORE, 'readwrite', store => reqToPromise(store.add(record)));
  } catch (_) {
    return null;
  }
}

export async function listOutbox(tripId) {
  try {
    const all = await tx(OUTBOX_STORE, 'readonly', store => {
      const idx = store.index('tripId');
      return reqToPromise(idx.getAll(key(tripId)));
    });
    // FIFO by auto-increment id.
    return (all || []).sort((a, b) => a.id - b.id);
  } catch (_) {
    return [];
  }
}

export async function dequeue(id) {
  try {
    await tx(OUTBOX_STORE, 'readwrite', store => reqToPromise(store.delete(id)));
  } catch (_) { /* ignore */ }
}

export async function outboxCount(tripId) {
  try {
    return await tx(OUTBOX_STORE, 'readonly', store => {
      const idx = store.index('tripId');
      return reqToPromise(idx.count(key(tripId)));
    });
  } catch (_) {
    return 0;
  }
}

// ── Trips list (per user) ────────────────────────────────────────────────────
// The home screen's trip list, cached so it loads instantly and survives offline.

export async function getTripsList(userKey) {
  try {
    const rec = await tx(LIST_STORE, 'readonly', store => reqToPromise(store.get(String(userKey))));
    return rec && Array.isArray(rec.trips) ? rec.trips : null;
  } catch (_) {
    return null;
  }
}

export async function saveTripsList(userKey, trips) {
  try {
    await tx(LIST_STORE, 'readwrite', store =>
      reqToPromise(store.put({ key: String(userKey), trips, savedAt: Date.now() })));
  } catch (_) { /* ignore */ }
}
